import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import { API } from "../constants/api";
import { PAGE } from "../constants/page";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { ApiCallWithLoader, errorUpdate } from "../redux/reducers/LoaderSlice";
import { stateRights } from "../config/stateRights";
import Utility from "../utility/Utility";
import "../../src/styles/ViewPatient.scss";
import SuccessMessage from "../components/SuccessMessage";
import { patientDetailsState } from "../redux/reducers/PatientSlice";
import i18n from "../i18n/config";

const ViewPatient = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const patientId: string = location.pathname.split("/").pop() || "";
  const [patientDetails, setPatientDetails] = useState<any>();
  const dispatch = useAppDispatch();
  const t_unavailable = i18n.t("unavailable");

  //load edit rights json file based on state
  const stateRight =
    stateRights[
      patientDetails?.address[0].state.toLowerCase().replace(/ /g, "")
    ];

  useEffect(() => {
    const fetchPatientDetails = async () => {
      const requestDetails = {
        url: API.PATIENT_DETAILS.replace(":id", patientId),
        method: "GET",
      };
      try {
        const resp = await dispatch(ApiCallWithLoader(requestDetails));

        if (resp) {
          setPatientDetails(resp?.payload);
          formik.setFieldValue(
            "district",
            resp.payload?.address[0]?.district || ""
          );
        }
        return true;
      } catch (error) {
        dispatch(errorUpdate({ isError: true, error: error }));
      }
    };
    fetchPatientDetails();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [patientDetails]);

  const handleGoback = () => {
    navigate(PAGE.VIEW_PATIENT_DETAILS.replace(":id", patientId));
  };

  const navigateToEditDetails = () => {
    navigate(PAGE.EDIT_PATIENT.replace(":id", patientId));
  };

  const updatedPatientDetails = useAppSelector(patientDetailsState);

  const formik = useFormik({
    initialValues: {
      gender: patientDetails ? patientDetails.gender : "",
      firstName: patientDetails ? patientDetails.name[0].given[0] : "",
      middleName: patientDetails && patientDetails.name[0]?.given[1] ? patientDetails.name[0].given[1] : "",
      lastName: patientDetails ? patientDetails.name[0].family : "",
      birthDate: patientDetails ? patientDetails.birthDate : "",
      idNumber:
        patientDetails && patientDetails?.identifier
          ? Utility.getIDNumber(patientDetails)
          : t_unavailable,
      mobileNumber:
        patientDetails && patientDetails?.telecom
          ? patientDetails.telecom[0].value
          : t_unavailable,
      id:
        patientDetails && patientDetails?.identifier
          ? patientDetails?.identifier[0]?.assigner?.display
          : t_unavailable,
      relationship: patientDetails?.contact
        ? patientDetails.contact[0]?.relationship[0].text
        : t_unavailable,
      name: patientDetails?.contact
        ? patientDetails?.contact[0]?.name?.given[0]
        : t_unavailable,
      contactNumber: patientDetails?.contact
        ? patientDetails?.contact[0]?.telecom[0]?.value
        : t_unavailable,
      state:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].state
          : t_unavailable,
      district:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0]?.district
          : t_unavailable,
      street1:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].line[0]
          : t_unavailable,
      street2:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].line[1]
          : t_unavailable,
      city:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].city
          : t_unavailable,
      pincode:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].postalCode
          : t_unavailable,
    },
    enableReinitialize: true,
    onSubmit: (values: any) => {},
  });

  return (
    <div className="viewpatient">
      <Breadcrumbs
        active_key="view_profile"
        navigation={navigate}
        location={location.pathname}
        routes={[
          {
            label_key: Utility.patientName(patientDetails?.name),
            fn: handleGoback,
          },
        ]}
      />
      {patientDetails && (
        <div className="container">
          {updatedPatientDetails.patientUpdated === true ? (
            <SuccessMessage
              patientProfile={<Trans i18nKey={"myProfile.profile"}></Trans>}
            />
          ) : null}
          <div className="align-layout">
            <h3 className="heading-bold mb-0 patient-header">
              <Trans i18nKey={"view_profile"}></Trans>
            </h3>
            <div className="text-end patient-button">
              {stateRight === undefined
                ? null
                : stateRight.edit_patient.hasEditRights && (
                    <Button
                      className="btn-edit-profile"
                      onClick={navigateToEditDetails}
                    >
                      <Trans i18nKey={"edit_profile"}></Trans>
                    </Button>
                  )}
            </div>
          </div>
          <div className="bg-light-gray">
            <h5 className="bg-light-blue p-2 view-heading">
              <Trans i18nKey={"basic_details"}></Trans>
            </h5>
            <div className="p-3 form-width">
              <label className="mt-0">
                <Trans i18nKey={"name"}></Trans>
              </label>
              <span className="p-label text-capitalize">
                {formik.values.firstName + ((formik.values.middleName) ? " " + formik.values.middleName : "") + " " + formik.values.lastName}
              </span>
              <label>
                <Trans i18nKey={"gender"}></Trans>
              </label>
              <span className="p-label text-capitalize">
                <Trans i18nKey={patientDetails?.gender}></Trans>
              </span>
              <label>
                <Trans i18nKey={"birth_date"}></Trans>
              </label>
              <span className="p-label">
                {Utility.formatDate(formik.values.birthDate)}
              </span>
              <label>
                <Trans i18nKey={"id_type"}></Trans>
              </label>
              <span className="p-label text-capitalize">
                <Trans i18nKey={formik.values.id}></Trans>
              </span>
              <label>
                <Trans i18nKey={"id_number"}></Trans>
              </label>
              <span className="p-label">{formik.values.idNumber}</span>
              <label>
                <Trans i18nKey={"mobile_number"}></Trans>
              </label>
              <span className="p-label">{formik.values.mobileNumber}</span>
            </div>
          </div>

          <div className="form-emergency-contact bg-light-gray mt-4 text-capitalize">
            <h5 className="bg-light-blue p-2 view-heading">
              <Trans i18nKey={"emergency_contact_details"}></Trans>
            </h5>
            <div className="p-3 form-width">
              <label className="mt-0">
                <Trans i18nKey={"name"}></Trans>
              </label>
              <span className="p-label">{formik.values.name}</span>
              <label>
                <Trans i18nKey={"relationship"}></Trans>
              </label>
              <span className="p-label">
                <Trans i18nKey={`list.${formik.values.relationship}`}></Trans>
              </span>
              <label>
                <Trans i18nKey={"contact_number"}></Trans>
              </label>
              <span className="p-label">{formik.values.contactNumber}</span>
            </div>
          </div>
          <div className="form-address bg-light-gray mt-4">
            <h5 className="bg-light-blue p-2 view-heading">
              <Trans i18nKey={"address_details"}></Trans>
            </h5>
            <div className="p-3 form-width">
              <label className="mt-0">
                <Trans i18nKey={"street1"}></Trans>
              </label>
              <span className="p-label">{formik.values.street1}</span>
              <label>
                <Trans i18nKey={"street2"}></Trans>
              </label>
              <span className="p-label">
                {formik.values.street2 ? (
                  formik.values.street2
                ) : (
                  <Trans i18nKey={"unavailable"}></Trans>
                )}
              </span>
              <label>
                <Trans i18nKey={"city_village"}></Trans>
              </label>
              <span className="p-label text-capitalize">
                {formik.values.city}
              </span>
              <label>
                <Trans i18nKey={"district"}></Trans>
              </label>
              <span className="p-label text-capitalize">
                {formik.values.district}
              </span>
              <label>
                <Trans i18nKey={"myProfile.state"}></Trans>
              </label>
              <span className="p-label text-capitalize">
                {formik.values.state}
              </span>
              <label>
                <Trans i18nKey={"pincode"}></Trans>
              </label>
              <span className="p-label">{formik.values.pincode}</span>
            </div>
          </div>
          <div className="col text-end mt-4" style={{ marginBottom: "150px" }}>
            {stateRight === undefined
              ? null
              : stateRight.edit_patient.hasEditRights && (
                  <Button
                    className="btn-edit-profile"
                    onClick={navigateToEditDetails}
                  >
                    <Trans i18nKey={"edit_profile"}></Trans>
                  </Button>
                )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewPatient;
